import React, { useState } from 'react';
import { stageGateAPI } from '../../../api/services';

interface StageGateConfig {
  stage: string;
  gateType: 'approval' | 'testing';
  mandatory: boolean;
  defaultApprovers?: string[];
}

interface Props {
  taskId: string;
  taskTitle: string;
  projectId: string;
  targetStage: string;
  gates: StageGateConfig[];
  projectMembers: { userId: string; name: string }[];
  onSubmitted: () => void;
  onSkip: () => void;
  onClose: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
};

const StageGateDialog: React.FC<Props> = ({
  taskId, taskTitle, projectId, targetStage, gates, projectMembers, onSubmitted, onSkip, onClose,
}) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const gate = gates[0];
  const isMandatory = gates.some(g => g.mandatory);
  const isApproval = gate?.gateType === 'approval';

  const filteredMembers = projectMembers.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!selectedUser) {
      setError('Please select a person');
      return;
    }
    const member = projectMembers.find(m => m.userId === selectedUser);
    if (!member) return;

    setLoading(true);
    setError('');
    try {
      for (const g of gates) {
        await stageGateAPI.create({
          taskId,
          projectId,
          stage: targetStage,
          gateType: g.gateType,
          mandatory: g.mandatory,
          assignedToUserId: selectedUser,
          assignedToName: member.name,
          notes,
        });
      }
      onSubmitted();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isApproval ? 'bg-amber-50' : 'bg-blue-50'}`}>
              {isApproval ? (
                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                </svg>
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {isApproval ? 'Approval Required' : 'Testing Required'}
              </h3>
              <p className="text-xs text-gray-500">
                Before moving to <span className="font-medium">{STAGE_LABELS[targetStage] || targetStage}</span>
                {isMandatory && <span className="text-red-500 ml-1">(mandatory)</span>}
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <p className="text-sm text-gray-700 mb-1">Task: <span className="font-medium">{taskTitle}</span></p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {isApproval ? 'Select Approver' : 'Assign QA Tester'}
            </label>
            <input
              type="text"
              placeholder="Search members..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded mb-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded">
              {filteredMembers.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-400">No members found</div>
              )}
              {filteredMembers.map(m => (
                <button
                  key={m.userId}
                  onClick={() => setSelectedUser(m.userId)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${selectedUser === m.userId ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                >
                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center shrink-0">
                    {(m.name || '?')[0]}
                  </div>
                  <span className="truncate">{m.name}</span>
                  {selectedUser === m.userId && (
                    <svg className="w-4 h-4 text-blue-600 ml-auto shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={isApproval ? 'Context for the approver...' : 'What needs testing...'}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded resize-none h-16 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {!isMandatory && (
              <button
                onClick={onSkip}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded hover:bg-gray-50"
              >
                Skip
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={loading || !selectedUser}
              className={`px-4 py-1.5 text-sm text-white rounded ${
                isApproval
                  ? 'bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300'
                  : 'bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300'
              }`}
            >
              {loading ? 'Submitting...' : isApproval ? 'Request Approval' : 'Request Testing'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageGateDialog;
